import { useEffect, useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import {
  pointThStyle,
  pointInputStyle,
  pointSpanStyle,
  getPointButtonStyle,
  buttonContainerStyle,
  popoverContainerStyle,
  popoverTitleStyle,
  popoverOptionsContainerStyle,
  popoverLabelStyle,
  popoverButtonsContainerStyle,
  popoverCancelButtonStyle,
  popoverConfirmButtonStyle,
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  emptyRowStyle,
  infoTextStyle,
} from "../components/Style";
import PageLayout from "../components/PageLayout";
import { Student } from "../types/Student";
import { GRADE_FILTER_OPTIONS, GRADE_ORDER } from "../components/Grade";
import Popover from "../components/Popover";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import { renderClassBadges } from "../utils/classBadgeUtils";
import { ensureDefaultPointItems, listPointItems } from "../services/points";
import { PointItem } from "../types/PointItem";
import Tooltip from "../components/Tooltip";

const columnHelper = createColumnHelper<Student>();

function PointPage() {
  // State: Students
  const [students, setStudents] = useState<Student[]>([]);
  // State: Point settings
  const [pointItems, setPointItems] = useState<PointItem[]>([]);
  // State: Sort
  const [sorting, setSorting] = useState<SortingState>([
    { id: "grade", desc: false }
  ]);
  // State: Filters
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  
  // Popover state
  const [popoverOpenFor, setPopoverOpenFor] = useState<string | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<Record<string, string>>({});
  
  // Inline point editing state
  const [editingPointStudentId, setEditingPointStudentId] = useState<number | null>(null);
  const [editingPointValue, setEditingPointValue] = useState<string>("");

  // API에서 학생 목록 가져오기
  const fetchStudents = async () => {
    try {
      const data = await apiClient.getStudents();
      setStudents(data);
    } catch (error) {
      console.error("학생 목록 조회 실패:", error);
    }
  };

  // 포인트 설정 불러오기
  const fetchPointItems = async () => {
    ensureDefaultPointItems();
    const items = await listPointItems();
    setPointItems(items);
  };

  useEffect(() => {
    fetchStudents();
    fetchPointItems();
  }, []);

  // handleChangePoints
  const handleChangePoints = async (student: Student, change: number, selectedOption?: string) => {
    try {
      const newPoints = student.bonusPoints + change;
      const { id, classes, ...studentData } = student;
      
      // classIds 배열 생성
      const classIds = (classes || []).map((c: any) => c.id).filter(Boolean);
      
      const updateData: any = {
        ...studentData,
        bonusPoints: newPoints,
        classIds,
      };
      
      if (selectedOption) {
        updateData.reason = selectedOption;
      }
      
      await apiClient.updateStudent(student.id!, updateData);
      fetchStudents();
    } catch (error) {
      console.error("포인트 변경 실패:", error);
    }
  };

  // 반 목록 추출
  const getClassList = () => {
    const gradeFilter = columnFilters.find(f => f.id === 'grade');
    let filtered = students;
    if (gradeFilter && gradeFilter.value) {
      filtered = filtered.filter(s => s.grade === gradeFilter.value);
    }
    // 학생들의 모든 반 이름을 추출 (중복 제거)
    const classSet = new Set<string>();
    filtered.forEach(s => {
      (s.classes || []).forEach((c: any) => {
        classSet.add(c.className);
      });
    });
    return Array.from(classSet).sort();
  };

  // Base columns
  const baseColumns = useMemo(() => [
    columnHelper.accessor("studentNumber", {
      header: "출결번호",
      size: 70,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("name", {
      header: "이름",
      size: 70,
      cell: (info) => info.getValue(),
      sortingFn: "alphanumeric",
    }),
    columnHelper.accessor("grade", {
      header: "학년",
      size: 70,
      cell: (info) => info.getValue(),
      sortingFn: (rowA, rowB, columnId) => {
        const gradeA = GRADE_ORDER[rowA.getValue(columnId) as string] || 999;
        const gradeB = GRADE_ORDER[rowB.getValue(columnId) as string] || 999;
        if (gradeA !== gradeB) {
          return gradeA - gradeB;
        }
        return rowA.original.name.localeCompare(rowB.original.name);
      },
    }),
    columnHelper.accessor("classes", {
      header: "반",
      size: 200,
      cell: (info) => {
        const studentClasses = info.getValue() || [];
        if (studentClasses.length === 0) {
          return <span style={{ color: "#999", fontSize: 13 }}>없음</span>;
        }

        // 반 이름을 쉼표로 구분된 문자열로 변환
        const classNames = studentClasses.map((c: any) => c.className).join(', ');
        
        // renderClassBadges 사용 (allClasses는 studentClasses로 사용)
        return renderClassBadges(classNames, studentClasses);
      },
      sortingFn: (rowA, rowB) => {
        const classesA = rowA.original.classes || [];
        const classesB = rowB.original.classes || [];
        const firstClassA = classesA.find((c: any) => c.classCategory === 'regular')?.className || '';
        const firstClassB = classesB.find((c: any) => c.classCategory === 'regular')?.className || '';
        return firstClassA.localeCompare(firstClassB);
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue) return true;
        const studentClasses = row.getValue(columnId) as any[];
        return studentClasses.some((c: any) => c.className === filterValue);
      },
    }),
    columnHelper.accessor("bonusPoints", {
      header: "포인트",
      size: 35,
      cell: (info) => {
        const student = info.row.original;
        const isEditing = editingPointStudentId === student.id;
        
        if (isEditing) {
          return (
            <input
              autoFocus
              type="number"
              value={editingPointValue}
              onChange={(e) => setEditingPointValue(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  const newValue = parseInt(editingPointValue, 10);
                  if (isNaN(newValue)) {
                    alert("올바른 숫자를 입력하세요.");
                    return;
                  }
                  try {
                    const { id, ...studentData } = student;
                    await apiClient.updateStudent(student.id!, {
                      ...studentData,
                      bonusPoints: newValue,
                    });
                    setEditingPointStudentId(null);
                    setEditingPointValue("");
                    fetchStudents();
                  } catch (error) {
                    console.error("포인트 수동 조정 실패:", error);
                    alert("포인트 조정에 실패했습니다.");
                  }
                } else if (e.key === 'Escape') {
                  setEditingPointStudentId(null);
                  setEditingPointValue("");
                }
              }}
              onBlur={async () => {
                if (editingPointValue === String(student.bonusPoints)) {
                  setEditingPointStudentId(null);
                  setEditingPointValue("");
                  return;
                }
                
                const newValue = parseInt(editingPointValue, 10);
                if (isNaN(newValue)) {
                  setEditingPointStudentId(null);
                  setEditingPointValue("");
                  return;
                }
                
                try {
                  const { id, ...studentData } = student;
                  await apiClient.updateStudent(student.id!, {
                    ...studentData,
                    bonusPoints: newValue,
                  });
                  setEditingPointStudentId(null);
                  setEditingPointValue("");
                  fetchStudents();
                } catch (error) {
                  console.error("포인트 수동 조정 실패:", error);
                  alert("포인트 조정에 실패했습니다.");
                  setEditingPointStudentId(null);
                  setEditingPointValue("");
                }
              }}
              style={pointInputStyle}
              placeholder="포인트"
            />
          );
        }
        
        return (
          <span
            onClick={() => {
              setEditingPointStudentId(student.id!);
              setEditingPointValue(String(student.bonusPoints));
            }}
            style={pointSpanStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e6f2ff";
              e.currentTarget.style.borderColor = "#0969da";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor = "transparent";
            }}
          >
            {student.bonusPoints}
          </span>
        );
      },
      sortingFn: "alphanumeric",
    }),
  ], [editingPointStudentId, editingPointValue]);

  // Point item columns
  const activePointItems = pointItems.filter(item => item.active !== false);
  const pointColumns = useMemo(() => 
    activePointItems.map((item) => 
      columnHelper.display({
        id: `point-${item.id}`,
        header: () => (
          <Tooltip content={item.title}>
            <span style={{ cursor: "help", borderBottom: "1px dotted #9ca3af" }}>
              {item.score > 0 ? `+${item.score}` : item.score}
            </span>
          </Tooltip>
        ),
        size: 50,
        cell: (info) => {
          const student = info.row.original;
          const hasPopover = (item.popoverItems?.length ?? 0) > 0;
          const popoverKey = `${student.id}-${item.id}`;

          if (hasPopover) {
            return (
              <div style={buttonContainerStyle}>
                <Popover
                  reference={
                    <button style={getPointButtonStyle(item.score)}>
                      {item.score > 0 ? `+${item.score}` : item.score}
                    </button>
                  }
                placement="right"
                open={popoverOpenFor === popoverKey}
                onOpenChange={(open) => {
                  if (open) {
                    setPopoverOpenFor(popoverKey);
                    setSelectedReasons((prev) => ({ ...prev, [popoverKey]: prev[popoverKey] ?? "" }));
                  } else {
                    setPopoverOpenFor(null);
                  }
                }}
                id={`popover-${popoverKey}`}
                className="point-popover"
                offset={8}
              >
                <div style={popoverContainerStyle}>
                  <div style={popoverTitleStyle}>
                    선택
                  </div>
                  <div style={popoverOptionsContainerStyle}>
                    {(item.popoverItems || []).map((popItem) => (
                      <label key={popItem} style={popoverLabelStyle}>
                        <input
                          type="radio"
                          name={`reason-${popoverKey}`}
                          value={popItem}
                          checked={selectedReasons[popoverKey] === popItem}
                          onChange={() => {
                            setSelectedReasons((prev) => ({ ...prev, [popoverKey]: popItem }));
                          }}
                        />
                        {popItem}
                      </label>
                    ))}
                  </div>
                  <div style={popoverButtonsContainerStyle}>
                    <button
                      onClick={() => setPopoverOpenFor(null)}
                      style={popoverCancelButtonStyle}
                    >
                      취소
                    </button>
                    <button
                      onClick={() => {
                        const reason = selectedReasons[popoverKey];
                        handleChangePoints(student, item.score, reason);
                        setPopoverOpenFor(null);
                      }}
                      style={popoverConfirmButtonStyle}
                    >
                      확인
                    </button>
                  </div>
                </div>
              </Popover>
              </div>
            );
          } else {
            return (
              <div style={buttonContainerStyle}>
                <button
                  onClick={() => handleChangePoints(student, item.score)}
                  style={getPointButtonStyle(item.score)}
                >
                  {item.score > 0 ? `+${item.score}` : item.score}
                </button>
              </div>
            );
          }
        },
      })
    ),
  [activePointItems, popoverOpenFor, selectedReasons]);

  // All columns
  const columns = useMemo(() => [...baseColumns, ...pointColumns], [baseColumns, pointColumns]);

  // Table instance
  const table = useReactTable({
    data: students,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <PageLayout 
      title="포인트 관리"
      actionRows={[
        <p style={infoTextStyle}>
          💡 학생의 포인트를 클릭하여 점수를 수정할 수 있습니다.
        </p>
      ]}
    >
      <style>
        {`
          .point-table tbody tr:hover {
            background-color: #f6f8fa;
          }
          .point-table input[type="number"]::-webkit-outer-spin-button,
          .point-table input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .point-table input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table 
          className="point-table"
          style={tanstackTableStyle}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  const isPointColumn = columnId.startsWith('point-');
                  
                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(isPointColumn, header.getSize(), pointThStyle)}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}
                          
                          {/* Filter row */}
                          {!isPointColumn && (
                            <div style={filterContainerStyle}>
                              {columnId === "studentNumber" && (
                                <TableInput
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(e) => header.column.setFilterValue(e.target.value)}
                                  placeholder="검색"
                                />
                              )}
                              {columnId === "name" && (
                                <TableInput
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(e) => header.column.setFilterValue(e.target.value)}
                                  placeholder="검색"
                                />
                              )}
                              {columnId === "grade" && (
                                <Dropdown
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(value) => header.column.setFilterValue(value || undefined)}
                                  options={[
                                    { value: "", label: "전체" },
                                    ...GRADE_FILTER_OPTIONS,
                                  ]}
                                />
                              )}
                              {columnId === "class" && (
                                <Dropdown
                                  value={(header.column.getFilterValue() as string) ?? ""}
                                  onChange={(value) => header.column.setFilterValue(value || undefined)}
                                  options={[
                                    { value: "", label: "전체" },
                                    ...getClassList().map((c) => ({ value: c, label: c })),
                                  ]}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={emptyRowStyle}
                >
                  학생이 없습니다.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  style={tanstackTrStyle}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isBonusPointsCell = cell.column.id === 'bonusPoints';
                    const isPointActionCell = cell.column.id.startsWith('point-');
                    const isEditableCell = isBonusPointsCell || isPointActionCell;
                    return (
                      <td
                        key={cell.id}
                        style={getTanstackTdStyle(isEditableCell, cell.column.getSize())}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}

export default PointPage;
